import { useTranslation } from 'react-i18next'

import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material'

import responsiveHook from '@hooks/useResponsive'

import { BulletPoints } from '@pages/LandingPage/Features/components/BulletPoints'

import { TLGTypography } from '@components/TLGTypography'

import { scaleUpTransition } from '@utils/styles'

import {
  containerSx,
  contentGridSx,
  dividerWrapperSx,
  modernizeYourLeadGenerationForTheEvolvingItBuyerBulletPointsSx,
  modernizeYourLeadGenerationForTheEvolvingItBuyerLeftSideImageSx,
  modernizeYourLeadGenerationForTheEvolvingItBuyerLeftSideWrapperSx,
  modernizeYourLeadGenerationForTheEvolvingItBuyerRightSideContentSx,
  modernizeYourLeadGenerationForTheEvolvingItBuyerRightSideWrapperSx,
  titleSx,
  unlockActionableLeadsAndCloseMoreDealsBulletPointsSx,
  unlockActionableLeadsAndCloseMoreDealsLeftSideContentSx,
  unlockActionableLeadsAndCloseMoreDealsLeftSideWrapperSx,
  unlockActionableLeadsAndCloseMoreDealsRightSideImageSx,
  unlockActionableLeadsAndCloseMoreDealsRightSideWrapperSx,
  unlockPowerAuthenticConnectionsBulletPointsSx,
  unlockPowerAuthenticConnectionsSx,
  unlockThePowerOfAuthenticConnectionsLeftSideContentSx,
  unlockThePowerOfAuthenticConnectionsLeftSideWrapperSx,
  unlockThePowerOfAuthenticConnectionsRightSideImageSx,
  unlockThePowerOfAuthenticConnectionsRightSideWrapperSx,
} from './styles'

const Content1Section = () => {
  const { t } = useTranslation()

  const { isDesktop, ifDesktop } = responsiveHook.useResponsive()

  return (
    <Box data-testid="tech-vendors-features-content-1-section" sx={containerSx}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={contentGridSx}
      >
        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={unlockActionableLeadsAndCloseMoreDealsLeftSideWrapperSx}
        >
          <Stack sx={unlockActionableLeadsAndCloseMoreDealsLeftSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Unlock actionable leads and close more deals')}
            </TLGTypography>
            <Typography>
              {t(
                'Increase your sales conversion rates and maximize your revenue potential by focusing on leads that are highly likely to convert.'
              )}
            </Typography>

            <BulletPoints
              list={[
                t(
                  'Receive leads from Tech Customers who have reviewed your information and clicked "Accept Match," indicating their genuine interest in your solutions.'
                ),
                t(
                  'By prioritizing leads that are inherently more closable, you can focus your efforts on prospects with a higher probability of conversion, resulting in improved sales performance.'
                ),
                t(
                  'Save time and resources by engaging with leads that align with your specific offerings, ensuring a more efficient sales process and higher return on investment.'
                ),
              ]}
              containerSx={unlockActionableLeadsAndCloseMoreDealsBulletPointsSx}
            />
          </Stack>
        </Box>

        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={unlockActionableLeadsAndCloseMoreDealsRightSideWrapperSx}
        >
          <Box
            alt={t('Lead profile page screenshot')}
            loading="lazy"
            component="img"
            src="/images/lead-profile-page.webp"
            sx={
              {
                ...unlockActionableLeadsAndCloseMoreDealsRightSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        {isDesktop && (
          <Box gridColumn="span 12" sx={dividerWrapperSx}>
            <Divider />
          </Box>
        )}
        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={modernizeYourLeadGenerationForTheEvolvingItBuyerLeftSideWrapperSx}
        >
          <Box
            alt={t('Tech vendor conversations page screenshot')}
            loading="lazy"
            component="img"
            src="/images/tech-vendor-conversations-page.webp"
            sx={
              {
                ...modernizeYourLeadGenerationForTheEvolvingItBuyerLeftSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={
            modernizeYourLeadGenerationForTheEvolvingItBuyerRightSideWrapperSx
          }
        >
          <Stack
            sx={
              modernizeYourLeadGenerationForTheEvolvingItBuyerRightSideContentSx
            }
          >
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t(
                'Modernize Your Lead Generation for the Evolving Tech Customer'
              )}
            </TLGTypography>
            <Typography>
              {t(
                'Align with the digital age & engage with Tech Customers in the way they want to connect.'
              )}
            </Typography>

            <BulletPoints
              list={[
                t(
                  'The Tech Customer landscape has evolved, with Millennials and Gen Z now playing a significant role.'
                ),
                t(
                  'Our platform caters to their preferences and needs, giving you a competitive edge in engaging with this tech-savvy audience.'
                ),
                t(
                  'The modern Tech Customer demands self-service options throughout their buying journey. Our marketplace enables them to explore, research, and evaluate solutions on their own terms, just the way they like to.'
                ),
                t(
                  'Position yourself to engage with Tech Customers in their preferred digital channels, ensuring your visibility and accessibility throughout their buying process.'
                ),
              ]}
              containerSx={
                modernizeYourLeadGenerationForTheEvolvingItBuyerBulletPointsSx
              }
            />
          </Stack>
        </Box>

        {isDesktop && (
          <Box gridColumn="span 12" sx={dividerWrapperSx}>
            <Divider />
          </Box>
        )}

        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={unlockThePowerOfAuthenticConnectionsLeftSideWrapperSx}
        >
          <Stack sx={unlockThePowerOfAuthenticConnectionsLeftSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={unlockPowerAuthenticConnectionsSx}
            >
              {t('Unlock the Power of Authentic Connections')}
            </TLGTypography>
            <Typography>
              {t(
                'Build Genuine Relationships with Tech Customers and Drive Successful Partnerships.'
              )}
            </Typography>

            <BulletPoints
              list={[
                t(
                  'Connect with Tech Customers who have chosen you as a match, indicating their genuine interest in forming a partnership with you and enabling more meaningful and productive interactions.'
                ),
                t(
                  'The leads you receive are from Tech Customers who have expressed a genuine desire to connect with you, providing you with higher-quality leads that are more likely to convert.'
                ),
                t(
                  'By giving Tech Customers the freedom to choose and initiate connections, you establish a foundation of trust and respect, leading to more fruitful relationships and long-term customer loyalty.'
                ),
              ]}
              containerSx={unlockPowerAuthenticConnectionsBulletPointsSx}
            />
          </Stack>
        </Box>

        <Box
          gridColumn={{
            xs: 'span 12',
            lg: 'span 6',
          }}
          sx={unlockThePowerOfAuthenticConnectionsRightSideWrapperSx}
        >
          <Box
            alt={t<string>('Tech vendor company profile page screenshot')}
            loading="lazy"
            component="img"
            src="/images/tech-vendor-company-profile.webp"
            sx={{
              ...unlockThePowerOfAuthenticConnectionsRightSideImageSx,
              ...ifDesktop(scaleUpTransition),
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export { Content1Section }
